import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../context/AuthContext";
import { recordLegalAction } from "../../api/legal";
import {
  LEGAL_REQUIRED_ACTIONS,
  LEGAL_SUBMISSION_ACTIONS,
} from "../../utils/legal/legalStatus";

/**
 * Shared logic for the dashboard's legal-update flow.
 *
 * `outstandingDocuments` are the documents the signed-in user still has a
 * required action on — keyed off the server-computed `status === "pending"`,
 * NOT `allowedActions` (which is populated for any acknowledgement/consent
 * document regardless of whether it has been accepted). `acknowledgeAll`
 * records the appropriate action for each and refreshes the auth context, at
 * which point the outstanding list clears.
 *
 * Used by both the blocking overlay and the in-document "Agree and continue"
 * buttons so they behave identically.
 */
export function useLegalUpdate() {
  const { t } = useTranslation(["common", "legal"]);
  const { legalStatus, accountContext, refreshContext, applyLegalStatus } =
    useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const outstandingDocuments = useMemo(() => {
    const documents = legalStatus?.documents
      ? Object.values(legalStatus.documents)
      : [];

    return documents.filter(
      (document) =>
        document &&
        document.currentAcceptanceValid !== true &&
        (document.status === "pending" || document.status === "declined"),
    );
  }, [legalStatus]);

  const resolveAction = (document) => {
    const allowed = document.allowedActions || [];
    const requiredAction = document?.requiredAction;

    if (allowed.includes(LEGAL_SUBMISSION_ACTIONS.ACKNOWLEDGED)) {
      return LEGAL_SUBMISSION_ACTIONS.ACKNOWLEDGED;
    }

    if (allowed.includes(LEGAL_SUBMISSION_ACTIONS.CONSENTED)) {
      return LEGAL_SUBMISSION_ACTIONS.CONSENTED;
    }

    if (requiredAction === LEGAL_REQUIRED_ACTIONS.ACKNOWLEDGEMENT) {
      return LEGAL_SUBMISSION_ACTIONS.ACKNOWLEDGED;
    }

    if (requiredAction === LEGAL_REQUIRED_ACTIONS.RENEWED_CONSENT) {
      return LEGAL_SUBMISSION_ACTIONS.CONSENTED;
    }

    return LEGAL_SUBMISSION_ACTIONS.PRESENTED;
  };

  const acknowledgeAll = useCallback(async () => {
    if (outstandingDocuments.length === 0) {
      return true;
    }

    setSubmitting(true);
    setError("");

    try {
      let nextLegalStatus = null;

      for (const document of outstandingDocuments) {
        const payload = {
          documentType: document.documentType,
          version: document.version,
          locale: document.locale || legalStatus?.locale || "en-GB",
          action: resolveAction(document),
          actionMethod: "in_app",
          clinicStudyId:
            legalStatus?.clinicStudyId ||
            accountContext?.profile?.clinicStudyId ||
            null,
        };
        const response = await recordLegalAction(payload);

        if (response?.legalStatus) {
          nextLegalStatus = response.legalStatus;
        }
      }

      let refreshedContext = null;
      if (!nextLegalStatus && refreshContext) {
        refreshedContext = await refreshContext();
        nextLegalStatus = refreshedContext?.legalStatus || null;
      } else if (refreshContext) {
        void Promise.resolve(refreshContext());
      }

      if (nextLegalStatus) {
        applyLegalStatus?.(nextLegalStatus);
      }
      return true;
    } catch (caughtError) {
      const errorMessage =
        caughtError?.response?.data?.error?.message ||
        caughtError?.response?.data?.error ||
        caughtError?.message ||
        "";
      if (process.env.NODE_ENV !== "test") {
        console.error("[legal-update] acknowledgeAll failed", {
          errorMessage: errorMessage || null,
          response: caughtError?.response?.data || null,
        });
      }

      setError(
        errorMessage ||
          t("overlay.error", {
            ns: "legal",
            defaultValue: "We couldn't save your response. Please try again.",
          }),
      );
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [
    outstandingDocuments,
    legalStatus,
    accountContext,
    refreshContext,
    applyLegalStatus,
    t,
  ]);

  // Records a `declined` action for outstanding documents that allow it, for
  // the audit trail, before the caller signs the user out. Best-effort; a
  // declined document remains outstanding so the user stays blocked until they
  // agree on a later sign-in.
  const declineAll = useCallback(async () => {
    const declinable = outstandingDocuments.filter((document) =>
      (document.allowedActions || []).includes(
        LEGAL_SUBMISSION_ACTIONS.DECLINED,
      ),
    );

    for (const document of declinable) {
      try {
        await recordLegalAction({
          documentType: document.documentType,
          version: document.version,
          locale: document.locale || legalStatus?.locale || "en-GB",
          action: LEGAL_SUBMISSION_ACTIONS.DECLINED,
          actionMethod: "in_app",
          clinicStudyId:
            legalStatus?.clinicStudyId ||
            accountContext?.profile?.clinicStudyId ||
            null,
        });
      } catch {
        // Ignore — best-effort audit record on sign-out.
      }
    }
  }, [outstandingDocuments, legalStatus, accountContext]);

  return {
    outstandingDocuments,
    hasBlockingUpdate: legalStatus?.appUseBlocked === true,
    submitting,
    error,
    acknowledgeAll,
    declineAll,
  };
}
